/**
 * /me/asset-approvals — a (non-HR) manager's asset requisition queue (ADR-0024
 * Phase 4, D2). The HR worklist is HR-gated, so a manager who only holds the
 * `employee` role reaches their routed requests through this dual-mount view —
 * the same discoverability lesson as recruitment/leave/expense approvals.
 * Approving the NEED here is distinct from the custodian's issue step.
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { meLayoutRoute } from './meLayout.js';
import { listApprovals, approveRequest, rejectRequest, type Assignment } from '../api/assets.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { TabButton } from './assetShared.js';

export const meAssetApprovalsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/asset-approvals',
  component: MeAssetApprovalsPage,
});

function MeAssetApprovalsPage(): JSX.Element {
  const qc = useQueryClient();
  const [view, setView] = useState<'pending_my_approval' | 'all'>('pending_my_approval');
  const query = useQuery({ queryKey: ['me', 'asset-approvals', view], queryFn: () => listApprovals(view) });
  const rows = query.data?.assignments ?? [];
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['me', 'asset-approvals'] });

  return (
    <section className="p-8 space-y-6 max-w-2xl">
      <header className="space-y-1">
        <h1 className="font-serif text-3xl">Asset approvals</h1>
        <p className="text-sm text-gray-400">Requisitions routed to you for approval. Approving confirms the need — a custodian issues the specific asset afterwards.</p>
      </header>
      <div className="flex gap-1.5">
        <TabButton active={view === 'pending_my_approval'} onClick={() => setView('pending_my_approval')}>Awaiting my decision</TabButton>
        <TabButton active={view === 'all'} onClick={() => setView('all')}>All I can see</TabButton>
      </div>
      {query.isLoading ? <p className="text-sm text-gray-400">Loading…</p> : null}
      <ul className="space-y-2">
        {rows.map((a) => <ApprovalRow key={a.id} a={a} onChanged={invalidate} />)}
        {rows.length === 0 && !query.isLoading ? <li className="text-sm text-gray-500">Nothing awaiting your decision.</li> : null}
      </ul>
    </section>
  );
}

function ApprovalRow({ a, onChanged }: { a: Assignment; onChanged: () => void }): JSX.Element {
  const approve = useMutation({ mutationFn: () => approveRequest(a.id), onSuccess: onChanged });
  const reject = useMutation({ mutationFn: () => rejectRequest(a.id), onSuccess: onChanged });
  const who = a.employee ? `${a.employee.firstName} ${a.employee.lastName} (${a.employee.employeeNumber})` : a.employeeId;
  return (
    <li className="rounded border border-gray-900 px-4 py-3 text-sm space-y-2">
      <div className="flex items-center justify-between">
        <div><span className="text-gray-200">{who}</span> <span className="text-xs text-gray-500">· {a.category?.label ?? a.categoryId}</span></div>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => approve.mutate()} disabled={approve.isPending} className="rounded bg-white text-black px-3 py-1.5 text-xs font-medium disabled:opacity-50">Approve need</button>
        <button type="button" onClick={() => reject.mutate()} disabled={reject.isPending} className="rounded border border-gray-700 text-gray-300 px-3 py-1.5 text-xs">Reject</button>
        <ApiErrorNotice error={approve.error ?? reject.error} fallback="Action failed." compact />
      </div>
    </li>
  );
}
